import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { api, USER_PREFERENCE_KEY } from '@/api';
import { queryClient } from '@/core/react-query';
import { useStudyGuard } from '@/shared/guard';
import {
  useFindingsByOrgan,
  useHiRadiologistKeyImages,
  useIsQualityReviewApplicable,
  useLatestRef,
  useUserPreferences,
} from '@/shared/hook';
import { ReportLink } from '@/shared/model';
import { useKeyImageLabelsStore, useReportValidationStore } from '@/store';

import { useAddendumTextForValidation } from './use-addendum-text-for-validation.hook';
import { useReportOrgans } from './use-report-organs.hook';
import { useValidateQualityControl } from './use-validate-quality-control.hook';
import { useValidateReport } from './use-validate-report.hook';

export function useUpfrontReportValidation(links: ReportLink[]) {
  const { studyId } = useStudyGuard();
  const { getPreference, isError: isPreferencesError } = useUserPreferences();
  const validateReport = useValidateReport(links);
  const { reportOrgans } = useReportOrgans();
  const isQualityReviewValid = useValidateQualityControl();
  const { keyImages } = useHiRadiologistKeyImages();
  const isQualityReviewApplicable = useIsQualityReviewApplicable();
  const addendumTextForValidation = useAddendumTextForValidation();

  const { defaultQuestionStatus, links: linksValidationStatus } = useReportValidationStore(
    useShallow((state) => ({
      defaultQuestionStatus: state.defaultQuestionStatus,
      links: state.links,
    })),
  );
  const { defaultFindings, findings } = useFindingsByOrgan();
  const keyImageLabels = useKeyImageLabelsStore((state) => state.keyImageLabels);

  const showRequiredActionsUpfront =
    getPreference(USER_PREFERENCE_KEY.SHOW_REQUIRED_ACTIONS_UPFRONT)?.currentValue === true;

  api.fct.qualityControl.query.useGetQualityControl(
    { payload: { studyId } },
    { enabled: showRequiredActionsUpfront && isQualityReviewApplicable },
  );

  const linksValidationStatusRef = useLatestRef(linksValidationStatus);
  const validateReportRef = useLatestRef(validateReport);

  const prevFindingsRef = useRef(findings);
  const prevDefaultFindingsRef = useRef(defaultFindings);

  useEffect(() => {
    const findingsChanged = prevFindingsRef.current !== findings;
    const defaultFindingsChanged = prevDefaultFindingsRef.current !== defaultFindings;

    prevFindingsRef.current = findings;
    prevDefaultFindingsRef.current = defaultFindings;

    if ((!findingsChanged && !defaultFindingsChanged) || !showRequiredActionsUpfront) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: [api.fct.report.KEY.GET_REPORT_STUDY, studyId] });
  }, [findings, defaultFindings, showRequiredActionsUpfront, studyId]);

  useEffect(() => {
    if (isPreferencesError || !showRequiredActionsUpfront || !reportOrgans) {
      return;
    }

    validateReportRef.current(linksValidationStatusRef.current, reportOrgans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportOrgans,
    isQualityReviewValid,
    findings,
    defaultFindings,
    defaultQuestionStatus,
    showRequiredActionsUpfront,
    isPreferencesError,
    keyImages,
    keyImageLabels,
    addendumTextForValidation,
  ]);
}
